import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { yen } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";

export const Cart = ({
  items,
  onQty,
  onRemove,
  discount,
  setDiscount,
  onSubmit,
  submitLabel,
  title,
  subtitle,
  busy,
}) => {
  const { t } = useI18n();
  const subtotal = items.reduce((s, i) => s + i.price * i.qty, 0);
  const total = Math.max(subtotal - (Number(discount) || 0), 0);

  return (
    <div className="flex flex-col h-full" data-testid="cart-panel">
      <div className="px-6 pt-6">
        <h2 className="font-display text-lg font-semibold">{title}</h2>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3 min-h-[120px]">
        {items.length === 0 && (
          <div className="text-center text-muted-foreground py-10">
            <ShoppingBag className="mx-auto mb-2 opacity-40" size={32} />
            <p className="text-sm">{t("empty_cart")}</p>
          </div>
        )}
        {items.map((it) => (
          <div
            key={it.product_id}
            data-testid={`cart-item-${it.product_id}`}
            className="flex items-center gap-3 bg-gray-50 rounded-xl p-3"
          >
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{it.name}</p>
              <p className="text-xs text-muted-foreground num">
                {yen(it.price)} / {it.unit}
              </p>
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                data-testid={`cart-dec-${it.product_id}`}
                onClick={() => onQty(it.product_id, it.qty - 1)}
              >
                <Minus size={14} />
              </Button>
              <span className="w-8 text-center num text-sm">{it.qty}</span>
              <Button
                size="icon"
                variant="outline"
                className="h-8 w-8"
                data-testid={`cart-inc-${it.product_id}`}
                onClick={() => onQty(it.product_id, it.qty + 1)}
              >
                <Plus size={14} />
              </Button>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 text-destructive"
                data-testid={`cart-remove-${it.product_id}`}
                onClick={() => onRemove(it.product_id)}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-200 p-6 space-y-3 bg-white">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t("subtotal")}</span>
          <span className="num" data-testid="cart-subtotal">{yen(subtotal)}</span>
        </div>
        <div className="flex items-center justify-between text-sm gap-3">
          <span className="text-muted-foreground">{t("discount")}</span>
          <Input
            data-testid="discount-input"
            inputMode="numeric"
            value={discount}
            placeholder="0"
            onChange={(e) => setDiscount(e.target.value.replace(/[^0-9]/g, ""))}
            className="h-9 w-28 text-right num"
          />
        </div>
        <div className="flex items-center justify-between">
          <span className="font-display font-semibold">{t("total")}</span>
          <span className="num text-3xl font-semibold" data-testid="cart-total">
            {yen(total)}
          </span>
        </div>
        <Button
          data-testid="cart-submit-btn"
          disabled={items.length === 0 || busy}
          onClick={onSubmit}
          className="w-full h-14 text-base rounded-xl"
        >
          {submitLabel}
        </Button>
      </div>
    </div>
  );
};
